/**
 * EIN API-Client für alle /api-Aufrufe.
 *  - hängt den Bearer-Token automatisch an,
 *  - liest die Basis-URL aus der Laufzeit-Config (nicht hartkodiert),
 *  - behandelt 401/403 zentral: Session verwerfen + onUnauthorized-Hook.
 */
import { config } from "../config";
import { clearSession, getToken } from "../state/session";

export class ApiError extends Error {
  status: number;
  detail: string;
  body: unknown;
  constructor(status: number, detail: string, body: unknown) {
    super(detail || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

/** Wird bei 401/403 aufgerufen (z. B. Router → zur Login-Seite). */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void): void {
  onUnauthorized = fn;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "DELETE";
  body?: unknown;
  /** Token weglassen (z. B. access-check vor Login). */
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";

  const token = getToken();
  if (token && !opts.anonymous) headers["Authorization"] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${config.apiBaseUrl}${path}`, {
      method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
    });
  } catch (err) {
    if ((err as Error)?.name === "AbortError") throw err;
    throw new ApiError(0, "Netzwerkfehler — keine Verbindung zum Server.", err);
  }

  if (res.status === 401 || res.status === 403) {
    // Token zentral verwerfen; Hook entscheidet über Navigation.
    clearSession();
    if (onUnauthorized) onUnauthorized();
  }

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const detail =
      (data && typeof data === "object" && "detail" in data
        ? String((data as { detail: unknown }).detail)
        : "") || `Fehler ${res.status}`;
    throw new ApiError(res.status, detail, data);
  }
  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Roh-POST (z. B. Audio-Blob an /api/transcribe). Content-Type = Blob-Typ. */
async function postRaw<T>(path: string, blob: Blob): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": blob.type || "application/octet-stream",
  };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${config.apiBaseUrl}${path}`, {
      method: "POST",
      headers,
      body: blob,
    });
  } catch (err) {
    throw new ApiError(0, "Netzwerkfehler — keine Verbindung zum Server.", err);
  }
  if (res.status === 401 || res.status === 403) {
    clearSession();
    if (onUnauthorized) onUnauthorized();
  }
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) {
    const detail =
      (data && typeof data === "object" && "detail" in data
        ? String((data as { detail: unknown }).detail)
        : "") || `Fehler ${res.status}`;
    throw new ApiError(res.status, detail, data);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "POST", body }),
  del: <T>(path: string, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: "DELETE" }),
  postRaw,
};
